import { RadioGroup } from '@headlessui/react'

import Image from 'next/image'
import React, { Fragment, useEffect, useState } from 'react'
import { Field, FormSpy, useForm } from 'react-final-form'

import clsx from 'clsx'

import { Card } from '@infura/packages.ui/Card'
import { Heading } from '@infura/packages.ui/Heading'
import { Input } from '@infura/packages.ui/Input'
import { Layout, ThemeLayouts } from '@infura/packages.ui/Layout'
import { RadioCheckbox } from '@infura/packages.ui/Radio'
import { Select } from '@infura/packages.ui/Select'
import { SemanticIcon } from '@infura/packages.ui/SemanticIcon'
import { Text } from '@infura/packages.ui/Text'
import { ServiceTier } from '@infura/packages.uks'

import { ISurveyInputOptions, ISurveyRadioCardOption } from 'components/onboardingWelcome'

const RadioField = ({ checked, option }) => {
  return (
    <Card
      className={clsx(
        'bg-background-default hover:bg-background-alternative hover:border-primary-default group flex h-full cursor-pointer flex-row items-center border-gray-600 !bg-gray-800 !px-3 !py-2',
        checked && 'bg-background-alternative border-primary-default'
      )}
    >
      <RadioCheckbox
        checked={checked}
        className={clsx(
          'bg-background-default group-hover:border-background-default group-hover:bg-primary-default group-hover:ring-primary-default mr-2 group-hover:border-2 group-hover:ring-2',
          checked && '!ring-primary-default !border-background-default !bg-primary-default ring-2'
        )}
      />
      <span className='mr-auto'>
        <Text as='p' className='mb-0 mr-2'>
          {option.label}
        </Text>
        {option.subtitle ? (
          <Text as='p' className='mb-0' theme='sm'>
            {option.subtitle}
          </Text>
        ) : null}
      </span>
      <div>
        {option.logoSrc ? (
          <div className='relative flex h-10 w-10 items-center justify-center rounded-full'>
            <Image alt={option.label} fill src={option.logoSrc} />
          </div>
        ) : (
          option.icon && (
            <SemanticIcon
              className={clsx('min-w-fit', checked && 'fill-primary-default')}
              icon={option.icon}
              theme='none'
            />
          )
        )}
      </div>
    </Card>
  )
}

const OtherResponseTextField = ({
  errors,
  id,
  section,
  textarea,
  touched,
  values
}: {
  section: string
  values: any
  id: string
  textarea: { placeholder: string }
  errors: Record<string, string>
  touched: Record<string, string>
}) => {
  const { mutators } = useForm()
  const textName = `${section}_${id}`
  const otherSelected = values[section] === 'other'

  useEffect(() => {
    if (!otherSelected) {
      mutators.setTextToEmpty(textName)
    }
  }, [textName, otherSelected, mutators, id])

  return otherSelected ? (
    <span data-testid={textName}>
      <Field name={textName}>
        {({ input }) => (
          <Layout className='relative !gap-2 !pb-0 pt-4' theme='oneColumn'>
            <Input
              {...input}
              className='bg-gray-800 text-white hover:!border-gray-600 hover:!bg-gray-800 focus:!border-gray-600 focus:!bg-gray-800'
              disabled={!otherSelected}
              error={errors[textName] && touched[textName] ? errors[textName] : ''}
              label={
                <Text as='p' className='m-0' theme='sm'>
                  <span>Please describe</span>
                  <span className='text-primary ml-1'>*</span>
                </Text>
              }
              name={textName}
              placeholder={textarea.placeholder}
              type='text'
            />
          </Layout>
        )}
      </Field>
    </span>
  ) : (
    <></>
  )
}

const SurveyRadioOptions = ({ columns = 'twoColumn', options, section }) => (
  <span data-testid={`onboarding-${section}`}>
    <Field name={section}>
      {({ input }) => (
        <FormSpy subscription={{ submitting: true }}>
          {({ submitting }) => (
            <RadioGroup {...input} disabled={submitting}>
              <Layout className={'!gap-4 !py-0 md:!gap-2'} container='full' theme={columns as ThemeLayouts}>
                {options.map(option => (
                  <RadioGroup.Option key={option.id} value={option.id}>
                    {({ checked }) => <RadioField checked={checked} option={option} />}
                  </RadioGroup.Option>
                ))}
              </Layout>
            </RadioGroup>
          )}
        </FormSpy>
      )}
    </Field>
    {options.map(
      (option, index) =>
        option.textarea && (
          <FormSpy key={index} subscription={{ errors: true, touched: true, values: true }}>
            {({ errors, touched, values }) => (
              <OtherResponseTextField errors={errors} section={section} touched={touched} values={values} {...option} />
            )}
          </FormSpy>
        )
    )}
  </span>
)

const SurveyRadioCardOptions = ({
  optionCardStyle = '',
  options,
  optionsContainerGridStyle = '',
  section
}: ISurveyRadioCardOption) => {
  const numberWithCommas = x => x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  const renderHighlight = (id: string, highlight: string | undefined) => {
    if (!highlight) return null

    return (
      <div
        className={clsx(
          'absolute inset-x-0 top-0 flex h-7 items-center justify-center rounded-t-md text-xs font-medium',
          id === ServiceTier.CORE && 'bg-background-default text-text-alternative',
          id === ServiceTier.DEVELOPER && 'bg-background-alternative text-text-alternative'
        )}
      >
        {highlight}
      </div>
    )
  }

  return (
    <span data-testid={`onboarding-${section}`}>
      <Field name={section}>
        {({ input }) => (
          <RadioGroup {...input}>
            <div className={clsx('m-6 mt-0 grid gap-4 md:grid-cols-4', optionsContainerGridStyle)}>
              {options.map(option => {
                return (
                  <RadioGroup.Option
                    className={clsx(!option.highlight && 'md:mt-7', 'cursor-pointer')}
                    data-testid='tier'
                    key={option.id}
                    value={option.id}
                  >
                    {({ checked }) => (
                      // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
                      <Card
                        className={clsx(
                          '!bg-background-alternative hover:border-primary-default border-border-default group relative mb-2 mr-2 min-h-[277px] w-full xl:w-56',
                          optionCardStyle,
                          checked && '!border-primary-default border',
                          option.highlight ? 'min-h-[301px] pt-12' : 'pt-6',
                          option.id === ServiceTier.DEVELOPER && 'bg-developer-onboarding-gradient'
                        )}
                        theme='alternative'
                      >
                        {renderHighlight(option.id, option.highlight)}
                        <div className='mb-2 flex items-center'>
                          <RadioCheckbox
                            checked={checked}
                            className={clsx(
                              'group-hover:ring-primary-default group-hover:border-background-default  group-hover:bg-primary-default absolute right-6 group-hover:border-2 group-hover:ring-2',
                              option.highlight ? 'top-14' : 'top-8',
                              checked && '!ring-primary-default !border-background-default !bg-primary-default ring-2'
                            )}
                          />
                          <Heading data-testid='tier-name'>{option.label}</Heading>
                        </div>
                        <Heading className='mb-4 border-b border-gray-600 pb-4' data-testid='tier-price' theme='page'>
                          <span>
                            {option.subtitle?.includes('$')
                              ? `US$${numberWithCommas(option.subtitle.replace('US$', ''))}`
                              : option.subtitle}
                          </span>
                          {option.subtitle?.includes('$') ? (
                            <span className='!font-sans text-sm !font-normal'> /month</span>
                          ) : null}
                        </Heading>
                        <ul
                          className={'list-image-[url(/images/icons/icon-check.svg)] pl-6 text-sm'}
                          data-testid='sellingpoints'
                        >
                          {option?.infoList?.map((info, index) => (
                            <li className='mb-2 last:mb-0' key={index}>
                              {info}
                            </li>
                          ))}
                        </ul>
                      </Card>
                    )}
                  </RadioGroup.Option>
                )
              })}
            </div>
          </RadioGroup>
        )}
      </Field>
    </span>
  )
}

const SurveySetKeyOptions = ({ columns = 'oneColumn', options, section, userKeys }) => {
  const [userKeysList, setUserKeysList] = useState([])
  const { mutators } = useForm()
  const userKeysLength = Object.values(userKeys).length

  useEffect(() => {
    if (Object.values(userKeys).length > 0) {
      setUserKeysList(
        (Object as any).values(userKeys).map(key => {
          const updated = new Date(key.updated * 1000)

          return {
            description: `Updated at ${updated.getFullYear()}-${updated.getMonth() + 1}-${updated.getDate()}`,
            text: key.name,
            value: key.id
          }
        })
      )
    }
  }, [userKeys])

  useEffect(() => {
    if (userKeysList.length === 1) {
      // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
      mutators.setApiKey(userKeysList[0].value)
    }
  }, [userKeysList, mutators])

  if (userKeysLength === 0) {
    return (
      <Card data-testid={`onboarding-${section}`}>
        <Layout className='relative pb-0 pt-0' theme={columns as ThemeLayouts}>
          {options.map((option, index) => (
            <Field key={index} name={section}>
              {({ input }) => (
                <FormSpy subscription={{ errors: true, touched: true }}>
                  {({ errors, touched }) => (
                    <Input
                      {...input}
                      className='bg-gray-800 text-white hover:!border-gray-600 hover:!bg-gray-800 focus:!border-gray-600 focus:!bg-gray-800'
                      // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
                      error={errors[section] && touched[section] ? errors[section] : ''}
                      name={section}
                      placeholder={option?.textarea?.placeholder}
                      showErrorMessage={false}
                      type='text'
                    />
                  )}
                </FormSpy>
              )}
            </Field>
          ))}
        </Layout>
      </Card>
    )
  }

  if (userKeysLength === 1) {
    return <></>
  }

  if (userKeysLength > 1) {
    return (
      <span data-testid={`onboarding-${section}`}>
        <Layout className='relative !py-0 pt-0' theme={columns as ThemeLayouts}>
          {options.map((option, index) => (
            <Field key={index} name={section}>
              {({ input }) => (
                <Select {...input} className='w-full' options={userKeysList} placeholder='API key name' />
              )}
            </Field>
          ))}
        </Layout>
      </span>
    )
  }
}

export const SurveyInputOptions: React.FC<ISurveyInputOptions> = ({
  columns,
  optionCardStyle = '',
  options,
  optionsContainerGridStyle = '',
  section,
  type,
  userKeys
}) => {
  return (
    <Fragment>
      {type === 'radio' ? <SurveyRadioOptions columns={columns} options={options} section={section} /> : null}
      {type === 'setKey' ? (
        <SurveySetKeyOptions columns={columns} options={options} section={section} userKeys={userKeys} />
      ) : null}
      {type === 'radioCard' ? (
        <SurveyRadioCardOptions
          optionCardStyle={optionCardStyle}
          options={options}
          optionsContainerGridStyle={optionsContainerGridStyle}
          section={section}
        />
      ) : null}
    </Fragment>
  )
}
